import {fetchAuthSession} from 'aws-amplify/auth';
import Dexie from 'dexie';
import {normalizeApiError} from 'src/redux/normalizeApiError';
import {isEffectivelyOffline} from './connectivity';
import {offlineDb} from './db';
import {isOwnedSession} from './offlineSessionManager';
import {IDMAP_SNAPSHOT_KEY} from './snapshotKeys';
import type {OutboxExecutorContext} from './executorRegistry';
import {
  DependsOnFailedItemError,
  DeterministicExecutorError,
  getOutboxExecutor,
  isBarrierKind,
} from './executorRegistry';
import type {OutboxFailureKind, SyncItemResult, SyncRunResult} from './types';

const SYNC_LOCK_NAME = 'proact-offline-sync';
export const ALREADY_SYNCING_MESSAGE = 'A sync is already in progress.';
const STALE_SYNCING_MS = 60_000;
const BACKOFF_BASE_MS = 5_000;
const BACKOFF_CAP_MS = 300_000;

let isSyncing = false;

export const isSyncRunning = (): boolean => isSyncing;

/** Bumps a syncing item's claim timestamp so a long upload isn't reclaimed. */
export const heartbeatClaim = async (itemId: string): Promise<void> => {
  await offlineDb.transaction('rw', offlineDb.outbox, async () => {
    const current = await offlineDb.outbox.get(itemId);
    if (current?.status === 'syncing') {
      await offlineDb.outbox.update(itemId, {
        claimedAt: new Date().toISOString(),
      });
    }
  });
};

const classifyFailure = (
  rawError: unknown,
  status: number | undefined
): OutboxFailureKind => {
  if (rawError instanceof DeterministicExecutorError) {
    return 'deterministic';
  }
  if (status === undefined) {
    return 'transient';
  }
  if (status === 408 || status === 429 || status >= 500) {
    return 'transient';
  }
  if (status >= 400) {
    return 'deterministic';
  }
  return 'transient';
};

// A dependency failure is only permanent once its parent is gone or itself
// deterministic-failed; while the parent may still succeed, keep retrying.
const classifyDependsOnFailed = async (
  error: DependsOnFailedItemError
): Promise<OutboxFailureKind> => {
  const parent = error.findParent ? await error.findParent() : undefined;
  if (!parent) return 'deterministic';
  if (parent.status === 'pending' || parent.status === 'syncing') {
    return 'transient';
  }
  if (parent.status === 'failed' && parent.failureKind === 'transient') {
    return 'transient';
  }
  return 'deterministic';
};

// True when an earlier item in this session hasn't succeeded yet — barrier
// items wait rather than run ahead of unsynced predecessors.
const hasUnsyncedPredecessor = async (
  sessionId: string,
  seq: number
): Promise<boolean> => {
  const blocking = await offlineDb.outbox
    .where('[sessionId+seq]')
    .between([sessionId, Dexie.minKey], [sessionId, seq], true, false)
    .filter(item => item.status !== 'succeeded')
    .count();
  return blocking > 0;
};

const backoffMs = (attemptCount: number): number =>
  Math.min(2 ** attemptCount * BACKOFF_BASE_MS, BACKOFF_CAP_MS);

const failItem = (
  claimed: {attemptCount?: number},
  error: string,
  failureKind: OutboxFailureKind
): Record<string, unknown> => ({
  status: 'failed',
  error,
  failureKind,
  attemptCount: (claimed.attemptCount ?? 0) + 1,
  lastAttemptAt: new Date().toISOString(),
});

export const isFailedItemDue = (item: {
  failureKind?: OutboxFailureKind;
  attemptCount?: number;
  lastAttemptAt?: string;
}): boolean => {
  if (item.failureKind !== 'transient') {
    return false;
  }
  const lastMs = item.lastAttemptAt ? new Date(item.lastAttemptAt).getTime() : 0;
  return Date.now() - lastMs >= backoffMs(item.attemptCount ?? 0);
};

/**
 * Drains the session outbox in seq order (token refresh first, failures don't
 * block later items). `holdsLock` reclaims any `syncing` item vs only stale.
 */
const runSync = async (
  sessionId: string,
  holdsLock: boolean
): Promise<SyncRunResult> => {
  isSyncing = true;
  const startedAt = new Date().toISOString();
  const results: SyncItemResult[] = [];

  try {
    try {
      await fetchAuthSession({forceRefresh: true});
    } catch {
      throw new Error(
        'Failed to refresh the access token before syncing. Check your connection and try again.'
      );
    }

    const idmapRow = await offlineDb.snapshots.get([
      sessionId,
      IDMAP_SNAPSHOT_KEY,
    ]);
    const idmap: Record<string, string | number> =
      (idmapRow?.data as Record<string, string | number> | undefined) ?? {};

    const persistIdmap = async (): Promise<void> => {
      await offlineDb.snapshots.put({
        key: IDMAP_SNAPSHOT_KEY,
        sessionId,
        data: {...idmap},
        cachedAt: new Date().toISOString(),
      });
    };

    await offlineDb.outbox
      .where('sessionId')
      .equals(sessionId)
      .filter(item => {
        if (item.status !== 'syncing') {
          return false;
        }
        if (holdsLock) {
          return true;
        }
        const claimedAtMs = item.claimedAt
          ? new Date(item.claimedAt).getTime()
          : 0;
        return Date.now() - claimedAtMs >= STALE_SYNCING_MS;
      })
      .modify({status: 'pending'});

    const items = await offlineDb.outbox
      .where('[sessionId+seq]')
      .between([sessionId, Dexie.minKey], [sessionId, Dexie.maxKey])
      .filter(
        item =>
          item.status === 'pending' ||
          (item.status === 'failed' && isFailedItemDue(item))
      )
      .sortBy('seq');

    const applyIfStillSyncing = async (
      itemId: string,
      changes: Record<string, unknown>
    ): Promise<boolean> =>
      offlineDb.transaction('rw', offlineDb.outbox, async () => {
        const current = await offlineDb.outbox.get(itemId);
        if (current?.status !== 'syncing') {
          return false;
        }
        await offlineDb.outbox.update(itemId, changes);
        return true;
      });

    for (const item of items) {
      // Leave a blocked barrier pending (don't claim/fail it) so it retries on a
      // later run once its predecessors succeed.
      if (
        isBarrierKind(item.kind) &&
        (await hasUnsyncedPredecessor(sessionId, item.seq))
      ) {
        continue;
      }

      const claimed = await offlineDb.transaction(
        'rw',
        offlineDb.outbox,
        async () => {
          const current = await offlineDb.outbox.get(item.id);
          if (
            !current ||
            (current.status !== 'pending' && current.status !== 'failed')
          ) {
            return undefined;
          }

          await offlineDb.outbox.update(item.id, {
            status: 'syncing',
            error: undefined,
            claimedAt: new Date().toISOString(),
          });
          return current;
        }
      );

      if (!claimed) {
        continue;
      }

      const executor = getOutboxExecutor(claimed.kind);
      if (!executor) {
        const error = `No executor registered for kind "${claimed.kind}".`;
        if (
          await applyIfStillSyncing(
            item.id,
            failItem(claimed, error, 'deterministic')
          )
        ) {
          results.push({
            itemId: item.id,
            kind: claimed.kind,
            status: 'failed',
            error,
          });
        }
        continue;
      }

      const ctx: OutboxExecutorContext = {
        sessionId,
        resolveId: tempId => idmap[tempId],
        recordIdMapping: async (tempId, realId) => {
          idmap[tempId] = realId;
          await persistIdmap();
        },
        heartbeat: () => heartbeatClaim(item.id),
      };

      try {
        const {summary} = await executor(
          claimed.payload,
          claimed.clientSubmissionId,
          ctx
        );
        const applied = await applyIfStillSyncing(item.id, {
          status: 'succeeded',
          resultSummary: summary,
          syncedAt: new Date().toISOString(),
        });
        if (applied) {
          results.push({
            itemId: item.id,
            kind: claimed.kind,
            status: 'succeeded',
            summary,
          });
        }
      } catch (rawError) {
        const normalized = normalizeApiError(rawError);
        const message = normalized.message ?? 'Sync failed for this item.';
        const failureKind =
          rawError instanceof DependsOnFailedItemError
            ? await classifyDependsOnFailed(rawError)
            : classifyFailure(rawError, normalized.status);
        const applied = await applyIfStillSyncing(
          item.id,
          failItem(claimed, message, failureKind)
        );
        if (applied) {
          results.push({
            itemId: item.id,
            kind: claimed.kind,
            status: 'failed',
            error: message,
          });
        }
      }
    }

    return {
      sessionId,
      startedAt,
      finishedAt: new Date().toISOString(),
      succeeded: results.filter(r => r.status === 'succeeded').length,
      failed: results.filter(r => r.status === 'failed').length,
      results,
    };
  } finally {
    isSyncing = false;
  }
};

/**
 * Public entry point. `isSyncing` guards this tab; `navigator.locks` guards
 * other tabs, falling back to the flag alone where Web Locks is unavailable.
 */
export const syncOutbox = async (sessionId: string): Promise<SyncRunResult> => {
  if (isEffectivelyOffline()) {
    throw new Error('Cannot sync while offline.');
  }
  const session = await offlineDb.sessions.get(sessionId);
  if (!session || !isOwnedSession(session)) {
    throw new Error(
      'Cannot sync a session that does not belong to the signed-in user.'
    );
  }
  if (isSyncing) {
    throw new Error(ALREADY_SYNCING_MESSAGE);
  }

  if (typeof navigator !== 'undefined' && navigator.locks) {
    const outcome = await navigator.locks.request(
      SYNC_LOCK_NAME,
      {ifAvailable: true},
      async lock => {
        if (!lock) {
          return {locked: false as const};
        }
        return {locked: true as const, result: await runSync(sessionId, true)};
      }
    );

    if (!outcome.locked) {
      throw new Error(ALREADY_SYNCING_MESSAGE);
    }
    return outcome.result;
  }

  return runSync(sessionId, false);
};
